import { memo, useMemo, useRef, useState } from 'react'

import { ReportOffOutlined } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { Alert } from '@nativewaves/nw-cloud-sdk/smep'
import { useQuery } from '@tanstack/react-query'
import { Table as ReactTableType } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { Editor } from '@shared/components/Editor'
import { Table } from '@shared/components/Table'
import { BigSideDrawer, LoadingIndicator } from '@shared/layouts'
import { useColumns } from 'domains/Workspaces/Events/Event/Monitoring/Alerts'
import { eventMonitoringRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const [viewItem, setViewItem] = useState<Alert | null>(null)
  const tableInstance = useRef<ReactTableType<Alert>>(undefined)

  const { eventId } = eventMonitoringRoute.useParams()

  const eventQuery = useQuery(eventQueryKeys.detail({ eventId }))

  const { columns, search } = useColumns()

  const searchParams = useMemo(() => Object.entries(search), [search])

  const filteredAlerts = useMemo(() => {
    const alerts = eventQuery.data?.alerts || []

    if (!searchParams.length) return alerts

    return alerts.filter((alert) => searchParams.every(([key, value]) => alert[key as keyof Alert] === value))
  }, [eventQuery.data?.alerts, searchParams])

  const alertsByDate = useMemo(() => {
    if (filteredAlerts) {
      return [...filteredAlerts].sort((a, b) => {
        return new Date(b.timestamp * 1000).getTime() - new Date(a.timestamp * 1000).getTime()
      })
    }
  }, [filteredAlerts])

  if (eventQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!alertsByDate?.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <ReportOffOutlined />
        <Typography variant="heading-md">{t('domain:Workspace.Event.noActiveAlerts')}</Typography>
      </Box>
    )
  }

  return (
    <>
      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        data={alertsByDate || []}
        columns={columns}
        isInitialLoadingGlobal={eventQuery.isLoading}
        enableClickAction
        clickAction={(row) => setViewItem(row.original)}
        hideHeader
        autoSizeRows
      />

      <BigSideDrawer
        DrawerProps={{ open: !!viewItem, onClose: () => setViewItem(null) }}
        ContentBoxSx={{
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          pl: 9,
          pt: 4,
          bgcolor: 'background.levels.700',
        }}
        title={<Typography variant="heading-md">{t('domain:Workspace.Event.alertDetails')}</Typography>}
        progress={100}
      >
        <Editor value={viewItem} readOnly />
      </BigSideDrawer>
    </>
  )
}

export const Overview = memo(OverviewRaw)
